import { useEffect, useRef, useState } from 'react'
import useBBViewport from 'src/hooks/useBBViewport'
import { ResponsiveProps } from '../../../utils/types/components'

export type IFrameProps = {
  url: string
  screenReaderTitle: string
  height?: ResponsiveProps<number>
  fullHeight?: boolean
}

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface ResizeMessageData {
  type: string
  height?: number
}

const resolveHeight = (height: ResponsiveProps<number>, bp: Breakpoint): number => {
  if (typeof height === 'number') return height

  const order: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl']

  for (let i = order.indexOf(bp); i >= 0; i--) {
    if (height[order[i]] != null) return height[order[i]]!
  }
  return 500
}

const IFrame = ({ url, screenReaderTitle, height = { xs: 500 }, fullHeight = false }: IFrameProps) => {
  const viewport = useBBViewport()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const fallbackHeight = resolveHeight(height, (viewport ?? 'xs') as Breakpoint)
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null)
  const hasMeasuredHeight = fullHeight && measuredHeight != null
  const renderedHeight = hasMeasuredHeight ? measuredHeight : fallbackHeight

  useEffect(() => {
    if (!fullHeight) return

    // The Riverflow URL is a cross-origin Cloudflare Worker, so the expected origin has to come
    // from `url` itself rather than the page's own origin.
    const expectedOrigin = new URL(url, window.location.href).origin

    // A live window drag makes the embedded page fire a burst of resize messages, one per
    // intermediate width. Each is a genuinely valid height for that instant, but applying every
    // one immediately makes the iframe visibly chase a moving target. Debounce so only the height
    // for the width the user actually settles on gets committed.
    let applyTimeoutId: ReturnType<typeof setTimeout>

    const handleMessage = (event: MessageEvent<ResizeMessageData>) => {
      const iframe = iframeRef.current
      if (!iframe || event.source !== iframe.contentWindow || event.origin !== expectedOrigin) return

      if (event.data?.type === 'resize' && typeof event.data.height === 'number' && event.data.height > 0) {
        const height = event.data.height
        clearTimeout(applyTimeoutId)
        applyTimeoutId = setTimeout(() => setMeasuredHeight(height), 150)
      }
    }

    // The embedded page reports its height once on its own load; if that happens before this
    // effect has attached its listener (e.g. a cached embed loading faster than this bundle
    // hydrates), the report is lost and the iframe is stuck at the fallback height forever.
    // Request a fresh report now and on every iframe (re)load so the handshake completes no
    // matter which side is ready last — mirrors AppleRiverflow's same fix for the same race.
    const requestHeight = () => {
      iframeRef.current?.contentWindow?.postMessage({ type: 'getHeight' }, expectedOrigin)
    }

    const iframe = iframeRef.current
    window.addEventListener('message', handleMessage)
    iframe?.addEventListener('load', requestHeight)
    requestHeight()

    return () => {
      clearTimeout(applyTimeoutId)
      window.removeEventListener('message', handleMessage)
      iframe?.removeEventListener('load', requestHeight)
    }
  }, [fullHeight, url])

  useEffect(() => {
    if (!fullHeight) return

    const expectedOrigin = new URL(url, window.location.href).origin
    let resizeTimeoutId: ReturnType<typeof setTimeout>

    // The embedded page's own ResizeObserver can miss layout shifts triggered by the host page
    // resizing across breakpoints (no DOM mutation on its side, just a reflow) — ask it to
    // re-measure explicitly whenever the window resizes, rather than relying on that alone.
    const handleResize = () => {
      clearTimeout(resizeTimeoutId)
      resizeTimeoutId = setTimeout(() => {
        iframeRef.current?.contentWindow?.postMessage({ type: 'getHeight' }, expectedOrigin)
      }, 200)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(resizeTimeoutId)
      window.removeEventListener('resize', handleResize)
    }
  }, [fullHeight, url])

  return (
    <iframe
      ref={iframeRef}
      src={url}
      title={screenReaderTitle}
      style={{
        width: '100%',
        height: `${renderedHeight}px`,
        border: 'none',
        // No transition here: with scrolling disabled below, any animated lag between the box's
        // height and the embedded page's actual content height would hide content with no way to
        // reach it (unlike AppleRiverflow, which allows a native scrollbar during its own transition).
        ...(hasMeasuredHeight && { overflow: 'hidden' }),
      }}
      // Only suppresses the native scrollbar once a real measurement has been applied. Before the
      // first report — or permanently, if the embedded page never implements/sends one — a
      // scrollbar is the safe fallback; disabling it unconditionally would silently clip content
      // taller than the static height fields with no way to reach it.
      {...(hasMeasuredHeight && { scrolling: 'no' })}
      loading="lazy"
      sandbox="allow-same-origin allow-scripts allow-forms"
      referrerPolicy="strict-origin-when-cross-origin"
    />
  )
}

export default IFrame
